/**
 * RecordInBrowserDialog — lets a portal user record audio directly from
 * their browser microphone and save it as a recording.
 *
 * Uses MediaRecorder; the first permission grant happens at "Start
 * recording" time, not on dialog open, so users who just clicked the menu
 * accidentally don't get a mic prompt. The dialog picks the first
 * supported MIME in this preference order: audio/webm, audio/mp4,
 * audio/ogg, audio/wav. Backend already accepts all of those via mime
 * sniffing — see backend/src/recordings/storage/filesystem.ts MIME_EXT.
 *
 * Uploads via the existing POST /api/recordings endpoint with
 * `created_via=browser` so the Recordings tab can tell browser-mic rows
 * apart from file uploads.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordings, type RecordingRow } from '@/api/recordings';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { cn } from '@/lib/cn';
import { Mic, Square, RotateCcw, Play, Pause } from 'lucide-react';

type RecState = 'idle' | 'recording' | 'paused' | 'stopped';

/**
 * Resolve the first MIME the browser's MediaRecorder supports. Chrome +
 * Firefox produce WebM by default; Safari produces MP4; older Edge might
 * fall back to Ogg. Returning null means MediaRecorder is unusable — the
 * dialog shows an error in that case.
 */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
  for (const m of candidates) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  // MediaRecorder exists but doesn't admit any format — fall through to
  // default; most UAs still produce something valid when `mimeType` is
  // omitted even if `isTypeSupported` lies.
  return null;
}

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  customerId?: string | null;
  folderId?: string | null;
  onCreated: (row: RecordingRow) => void;
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function RecordInBrowserDialog({
  open, onOpenChange, customerId: initialCustomerId, folderId, onCreated,
}: Props) {
  const qc = useQueryClient();
  const [state, setState] = useState<RecState>('idle');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState<'en' | 'he' | 'yi'>('en');
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId ?? null);
  const [serverError, setServerError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  // Track accumulated pause time so the elapsed counter stays accurate.
  const pausedAtRef = useRef<number>(0);
  const totalPausedMsRef = useRef<number>(0);

  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  // Object URL for playback preview; recreated per blob.
  const previewUrl = blob ? URL.createObjectURL(blob) : null;

  // Cleanup: tear down audio elements, streams, timers, and object URLs
  // when the dialog closes or a new blob replaces the old one.
  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearInterval(timerRef.current);
      stopTracks();
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function stopTracks() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function startRecording() {
    setPermissionError(null);
    setServerError(null);
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPermissionError('Your browser does not support microphone recording.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => {
        // Use the recorder's advertised MIME if present; otherwise fall
        // back to whatever the first chunk declared. Some browsers leave
        // the `type` blank on chunks but populate it on the recorder.
        const recMime = rec.mimeType || chunksRef.current[0]?.type || 'audio/webm';
        const combined = new Blob(chunksRef.current, { type: recMime });
        setBlob(combined);
        setState('stopped');
        stopTracks();
        if (timerRef.current != null) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      startedAtRef.current = Date.now();
      totalPausedMsRef.current = 0;
      setElapsed(0);
      timerRef.current = window.setInterval(() => {
        setElapsed(Date.now() - startedAtRef.current - totalPausedMsRef.current);
      }, 200);
      setState('recording');
    } catch (err) {
      setPermissionError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone permission denied. Allow mic access and try again.'
          : 'Could not start the microphone. Is another app using it?',
      );
      setState('idle');
    }
  }

  function stopRecording() {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // If paused, accumulate the pause time before stopping so elapsed is correct.
      if (mediaRecorderRef.current.state === 'paused') {
        totalPausedMsRef.current += Date.now() - pausedAtRef.current;
      }
      mediaRecorderRef.current.stop();
    }
  }

  function pauseRecording() {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== 'recording') return;
    rec.pause();
    pausedAtRef.current = Date.now();
    // Stop the live timer — elapsed freezes while paused.
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setState('paused');
  }

  function resumeRecording() {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state !== 'paused') return;
    totalPausedMsRef.current += Date.now() - pausedAtRef.current;
    rec.resume();
    // Restart the live timer, subtracting accumulated pause time.
    timerRef.current = window.setInterval(() => {
      setElapsed(Date.now() - startedAtRef.current - totalPausedMsRef.current);
    }, 200);
    setState('recording');
  }

  function retake() {
    setBlob(null);
    setState('idle');
    setElapsed(0);
    setPlaying(false);
    if (audioRef.current) audioRef.current.pause();
  }

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  }

  const upload = useMutation({
    mutationFn: (fd: FormData) => recordings.upload(fd),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onCreated(row);
    },
    onError: (err) => {
      setServerError(err instanceof Error ? err.message : 'Upload failed.');
    },
  });

  const canSubmit =
    state === 'stopped'
    && !!blob
    && name.trim().length > 0
    && effectiveCustomerId !== null
    && !upload.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // CRITICAL: stop propagation so the submit doesn't bubble through the
    // React tree and trigger an outer <form onSubmit> (e.g. NodeEditorDrawer).
    // Radix Dialogs render into a portal, but React synthetic events still
    // bubble through the *React* tree. See UploadInlineDialog for the same
    // fix and a link to the React docs.
    e.stopPropagation();
    if (!canSubmit || !blob) return;
    setServerError(null);

    // Browser blob.type is usually 'audio/webm;codecs=opus' — strip
    // the codec suffix because the backend's MIME_EXT lookup matches
    // the bare type only.
    const bareMime = (blob.type || 'audio/webm').split(';')[0]!.trim();
    const ext = bareMime.split('/')[1] ?? 'webm';
    const filename = `${name.trim().replace(/[^\w.-]+/g, '_') || 'recording'}.${ext}`;

    const fd = new FormData();
    fd.append('file', new File([blob], filename, { type: bareMime }));
    fd.append('name', name.trim());
    fd.append('language', language);
    fd.append('created_via', 'browser');
    if (folderId) fd.append('folder_id', folderId);
    if (effectiveCustomerId) {
      fd.append('customer_id', effectiveCustomerId);
    }
    upload.mutate(fd);
  }

  function handleOpenChange(v: boolean) {
    if (upload.isPending) return;
    if (!v) {
      // Tear everything down on close.
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        // Resume first if paused — some browsers won't fire onstop otherwise.
        try {
          if (mediaRecorderRef.current.state === 'paused') mediaRecorderRef.current.resume();
          mediaRecorderRef.current.stop();
        } catch { /* ignore */ }
      }
      stopTracks();
      if (timerRef.current != null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setState('idle');
      setBlob(null);
      setElapsed(0);
      setPlaying(false);
      setName('');
      setPermissionError(null);
      setServerError(null);
    }
    onOpenChange(v);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record in browser</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-5">
            {customerPicker}
            <CustomerScopeChip />

            {/* Recorder UI */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className={cn(
                      'flex h-12 w-12 items-center justify-center rounded-full',
                      state === 'recording' && 'bg-red-500 text-white animate-pulse',
                      state === 'paused' && 'bg-amber-400 text-white',
                      (state === 'idle' || state === 'stopped') && 'bg-slate-200 text-slate-500',
                    )}
                  >
                    <Mic className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {state === 'idle' && 'Ready to record'}
                      {state === 'recording' && 'Recording…'}
                      {state === 'paused' && 'Paused'}
                      {state === 'stopped' && 'Recording captured'}
                    </p>
                    <p className="font-mono text-xs text-slate-500 tabular-nums">
                      {formatElapsed(elapsed)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {state === 'idle' && (
                    <Button type="button" size="sm" onClick={startRecording}>
                      <Mic className="h-4 w-4 mr-1.5" /> Start
                    </Button>
                  )}
                  {state === 'recording' && (
                    <>
                      <Button type="button" size="sm" variant="outline" onClick={pauseRecording}>
                        <Pause className="h-4 w-4 mr-1.5" /> Pause
                      </Button>
                      <Button type="button" size="sm" variant="destructive" onClick={stopRecording}>
                        <Square className="h-4 w-4 mr-1.5" /> Stop
                      </Button>
                    </>
                  )}
                  {state === 'paused' && (
                    <>
                      <Button type="button" size="sm" onClick={resumeRecording}>
                        <Mic className="h-4 w-4 mr-1.5" /> Resume
                      </Button>
                      <Button type="button" size="sm" variant="destructive" onClick={stopRecording}>
                        <Square className="h-4 w-4 mr-1.5" /> Stop
                      </Button>
                    </>
                  )}
                  {state === 'stopped' && (
                    <>
                      <Button type="button" size="sm" variant="outline" onClick={togglePlay}>
                        {playing
                          ? <><Pause className="h-4 w-4 mr-1.5" /> Pause</>
                          : <><Play className="h-4 w-4 mr-1.5" /> Play</>}
                      </Button>
                      <Button type="button" size="sm" variant="outline" onClick={retake}>
                        <RotateCcw className="h-4 w-4 mr-1.5" /> Retake
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {previewUrl && (
                <audio
                  ref={audioRef}
                  src={previewUrl}
                  onEnded={() => setPlaying(false)}
                  className="sr-only"
                />
              )}

              {permissionError && (
                <p role="alert" className="mt-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  {permissionError}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="br-name">Name <span aria-hidden>*</span></Label>
              <Input
                id="br-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Welcome greeting"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="br-lang">Language</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as typeof language)}>
                <SelectTrigger id="br-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="he">Hebrew</SelectItem>
                  <SelectItem value="yi">Yiddish</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {serverError && (
              <p role="alert" className="text-xs text-red-500">{serverError}</p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={upload.isPending}>Cancel</Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit}>
              {upload.isPending ? 'Saving…' : 'Save recording'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
